(() => {
  const roots = document.querySelectorAll('[data-jsk-auto-catalog]');
  const esc = (value) => String(value ?? '').replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch]);
  const tag = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const publicEvidence = (value) => String(value || '').toLowerCase() === 'not documented' ? 'Confirm before order' : value;

  roots.forEach(async (root) => {
    if (root.dataset.ready === 'true') return;
    root.dataset.ready = 'true';
    const grid = root.querySelector('[data-auto-grid]');
    const detail = root.querySelector('[data-auto-detail]');
    const summary = root.querySelector('[data-auto-summary]');
    const pagination = root.querySelector('[data-auto-pagination]');
    const search = root.querySelector('[data-auto-search]');
    const select = root.querySelector('[data-auto-taxonomy]');
    const groups = root.querySelector('[data-auto-groups]');
    const error = root.querySelector('[data-auto-error]');
    const clear = root.querySelector('[data-auto-clear]');
    const interest = root.querySelector('[data-auto-interest]');
    const mediaMap = JSON.parse(root.querySelector('[data-auto-media-map]').textContent);
    const pageSize = 12;
    const state = { query: '', taxonomy: 'all', page: 1, media: new Map() };
    let data;
    let lastInterestTrigger;

    try {
      const response = await fetch(root.dataset.catalogUrl, { credentials: 'same-origin' });
      if (!response.ok) throw new Error('catalogue request failed');
      data = await response.json();
    } catch (failure) {
      grid.setAttribute('aria-busy', 'false');
      summary.hidden = true;
      error.hidden = false;
      return;
    }

    data.taxonomy_groups.forEach((group) => {
      const option = document.createElement('option');
      option.value = group.slug;
      option.textContent = `${group.name} (${group.count})`;
      select.append(option);
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.group = group.slug;
      button.textContent = `${group.name} · ${group.count}`;
      groups.append(button);
    });
    const all = document.createElement('button');
    all.type = 'button';
    all.dataset.group = 'all';
    all.textContent = `All parts · ${data.product_count}`;
    groups.prepend(all);

    const productHref = (product) => {
      const url = new URL(window.location.href);
      url.search = '';
      url.hash = '';
      url.searchParams.set('product', product.code);
      return `${url.pathname}${url.search}`;
    };

    const mediaUrls = (product) => product.media.map((media) => mediaMap[media.asset_key]).filter(Boolean);

    const filtered = () => data.products.filter((product) => {
      const groupMatch = state.taxonomy === 'all' || product.taxonomy_slug === state.taxonomy;
      const haystack = [product.code, product.title, product.part_type, product.part_number, product.oem_reference, product.application_text, product.taxonomy].join(' ').toLowerCase();
      return groupMatch && (!state.query || haystack.includes(state.query));
    });

    const openInterest = (product, trigger) => {
      if (!product?.register_interest_safe) return;
      lastInterestTrigger = trigger || document.activeElement;
      interest.querySelector('[data-auto-interest-product]').textContent = `${product.title} (${product.code})`;
      interest.querySelector('[data-auto-interest-code]').value = product.code;
      interest.querySelector('[data-auto-interest-title]').value = product.title;
      interest.querySelector('[data-auto-interest-tags]').value = `newsletter,JSK Interest,Auto Parts Interest,interest-source-auto-parts,${product.code},interest-${tag(product.code)}`;
      interest.hidden = false;
      document.body.classList.add('jsk-auto-interest-open');
      interest.querySelector('input[type="email"]').focus();
    };

    const closeInterest = () => {
      interest.hidden = true;
      document.body.classList.remove('jsk-auto-interest-open');
      lastInterestTrigger?.focus?.();
    };

    const card = (product) => {
      const activeIndex = Math.min(state.media.get(product.code) || 0, product.media.length - 1);
      const media = product.media[activeIndex];
      const mediaUrl = mediaMap[media.asset_key];
      const href = productHref(product);
      const controls = product.media.length > 1
        ? `<div class="jsk-auto-card__media-controls"><button type="button" data-media-prev="${esc(product.code)}" aria-label="Previous image of ${esc(product.title)}">‹</button><span>${activeIndex + 1} / ${product.media.length}</span><button type="button" data-media-next="${esc(product.code)}" aria-label="Next image of ${esc(product.title)}">›</button></div>`
        : '';
      const action = product.register_interest_safe
        ? `<button type="button" class="button" data-interest-code="${esc(product.code)}">Check fitment &amp; availability</button>`
        : '<p class="jsk-auto-card__hold">Contact JSK with this code to confirm fitment and product details.</p>';
      return `<article class="jsk-auto-card" data-auto-card-code="${esc(product.code)}">` +
        `<div class="jsk-auto-card__media"><a class="jsk-auto-card__media-link" href="${esc(href)}" aria-label="View details for ${esc(product.title)}"><img src="${esc(mediaUrl)}" alt="${esc(product.title)}" width="720" height="540" loading="lazy"></a>${controls}</div>` +
        `<div class="jsk-auto-card__body"><div class="jsk-auto-card__top"><span>${esc(product.taxonomy)}</span><strong>${esc(product.code)}</strong></div>` +
        `<h2><a href="${esc(href)}">${esc(product.title)}</a></h2>` +
        `<p class="jsk-auto-card__reference">Reference: ${esc(product.part_number || product.oem_reference || 'Confirm before order')}</p>` +
        `<div class="jsk-auto-card__application"><span>Recorded application — confirm fitment</span><p>${esc(product.application_text)}</p></div>` +
        `<p class="jsk-auto-card__fitment">${esc(product.fitment_notice)}</p>` +
        `<div class="jsk-auto-card__status"><span>${esc(product.availability)}</span><strong>${esc(product.price_display)}</strong></div>` +
        `<details><summary>Product status</summary><dl><div><dt>Condition</dt><dd>${esc(publicEvidence(product.condition))}</dd></div><div><dt>Warranty</dt><dd>${esc(publicEvidence(product.warranty))}</dd></div><div><dt>Lead time</dt><dd>${esc(publicEvidence(product.lead_time))}</dd></div><div><dt>Images</dt><dd>${product.register_interest_safe ? 'Product image available' : 'Ask JSK to confirm this image'}</dd></div></dl></details>${action}</div></article>`;
    };

    const pageButtons = (pages) => {
      const values = [];
      for (let i = 1; i <= pages; i += 1) {
        if (i === 1 || i === pages || Math.abs(i - state.page) <= 2) values.push(i);
        else if (values[values.length - 1] !== '…') values.push('…');
      }
      return `<button type="button" data-page="${Math.max(1, state.page - 1)}" ${state.page === 1 ? 'disabled' : ''}>Previous</button>` +
        values.map((value) => value === '…' ? '<span aria-hidden="true">…</span>' : `<button type="button" data-page="${value}" ${value === state.page ? 'aria-current="page"' : ''}>${value}</button>`).join('') +
        `<button type="button" data-page="${Math.min(pages, state.page + 1)}" ${state.page === pages ? 'disabled' : ''}>Next</button>`;
    };

    const render = () => {
      const products = filtered();
      const pages = Math.max(1, Math.ceil(products.length / pageSize));
      state.page = Math.min(state.page, pages);
      const start = (state.page - 1) * pageSize;
      const shown = products.slice(start, start + pageSize);
      grid.innerHTML = shown.map(card).join('');
      grid.setAttribute('aria-busy', 'false');
      summary.textContent = products.length
        ? `Showing ${start + 1}–${start + shown.length} of ${products.length} parts · page ${state.page} of ${pages}`
        : 'No parts match these filters.';
      pagination.innerHTML = products.length ? pageButtons(pages) : '';
      groups.querySelectorAll('button').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.group === state.taxonomy)));
      select.value = state.taxonomy;
    };

    const renderDetail = (product) => {
      const urls = mediaUrls(product);
      const initialImage = urls[0] || '';
      const thumbs = urls.length > 1
        ? `<div class="jsk-auto-detail__thumbs" aria-label="Additional views">${urls.map((url, index) => `<button type="button" data-detail-image="${esc(url)}" aria-label="Show view ${index + 1} of ${esc(product.title)}" aria-pressed="${index === 0}"><img src="${esc(url)}" alt="" width="96" height="72"></button>`).join('')}</div>`
        : '';
      const imageState = product.register_interest_safe ? 'Product image available' : 'Ask JSK to confirm this image';
      const action = product.register_interest_safe
        ? `<button type="button" class="button" data-interest-code="${esc(product.code)}">Check fitment &amp; availability</button>`
        : '<p class="jsk-auto-detail__hold">Contact JSK with this code to confirm fitment and product details.</p>';
      const fact = (label, value) => `<div><dt>${esc(label)}</dt><dd>${esc(value)}</dd></div>`;
      detail.innerHTML = `<article class="jsk-auto-detail__glass" aria-labelledby="AutoPartTitle-${esc(product.code)}" tabindex="-1">` +
        '<div class="jsk-auto-detail__tools"><a href="' + esc(window.location.pathname) + '">← Back to Auto Parts</a><button type="button" data-auto-share>Share product</button></div>' +
        '<div class="jsk-auto-detail__layout"><div class="jsk-auto-detail__media">' +
        (initialImage ? `<button type="button" class="jsk-auto-detail__zoom" aria-label="Magnify image for ${esc(product.title)}" aria-pressed="false"><img src="${esc(initialImage)}" alt="${esc(product.title)}" width="960" height="720"></button>${thumbs}` : '<div class="jsk-auto-detail__media-pending">Contact JSK for product images.</div>') +
        `</div><div class="jsk-auto-detail__content"><p class="jsk-auto-detail__code">${esc(product.code)}</p><span class="jsk-auto-detail__status">${esc(product.availability)}</span>` +
        `<h1 id="AutoPartTitle-${esc(product.code)}">${esc(product.title)}</h1>` +
        `<p class="jsk-auto-detail__fitment">${esc(product.fitment_notice)}</p>` +
        `<dl class="jsk-auto-detail__facts">${fact('Product group', product.taxonomy)}${fact('Part type', product.part_type)}${fact('Part number', product.part_number || 'Confirm before order')}${fact('OEM reference', product.oem_reference || 'Confirm before order')}${fact('Recorded application', product.application_text)}${fact('Availability', product.availability)}${fact('Price', product.price_display)}${fact('Condition', publicEvidence(product.condition))}${fact('Warranty', publicEvidence(product.warranty))}${fact('Lead time', publicEvidence(product.lead_time))}${fact('Images', imageState)}</dl>` +
        `<div class="jsk-auto-detail__actions">${action}<a class="button button--secondary" href="${esc(window.location.pathname)}">Continue browsing</a></div>` +
        '<p class="jsk-auto-detail__note">No payment is collected on this page. JSK will confirm exact fitment, availability, condition, warranty, lead time, and final price before accepting an order.</p></div></div>' +
        '<section class="jsk-auto-detail__reviews"><h2>Customer reviews</h2><p>No verified customer reviews have been published for this item yet.</p></section></article>';
      detail.hidden = false;
      root.classList.add('is-detail-mode');
      document.title = `${product.title} | JSK Marketplace`;

      const primary = detail.querySelector('.jsk-auto-detail__zoom img');
      const zoom = detail.querySelector('.jsk-auto-detail__zoom');
      zoom?.addEventListener('click', () => {
        const pressed = zoom.getAttribute('aria-pressed') !== 'true';
        zoom.setAttribute('aria-pressed', String(pressed));
        zoom.setAttribute('aria-label', `${pressed ? 'Restore' : 'Magnify'} image for ${product.title}`);
      });
      detail.querySelectorAll('[data-detail-image]').forEach((button) => button.addEventListener('click', () => {
        if (!primary) return;
        primary.src = button.dataset.detailImage;
        detail.querySelectorAll('[data-detail-image]').forEach((item) => item.setAttribute('aria-pressed', String(item === button)));
      }));
      detail.querySelector('[data-auto-share]')?.addEventListener('click', async (event) => {
        const button = event.currentTarget;
        const shareData = { title: product.title, text: `${product.title} (${product.code})`, url: window.location.href };
        try {
          if (navigator.share) await navigator.share(shareData);
          else if (navigator.clipboard) {
            await navigator.clipboard.writeText(shareData.url);
            button.textContent = 'Link copied';
            window.setTimeout(() => { button.textContent = 'Share product'; }, 1800);
          }
        } catch (shareError) {
          if (shareError?.name !== 'AbortError') button.textContent = 'Share unavailable';
        }
      });
      detail.querySelector('[data-interest-code]')?.addEventListener('click', (event) => openInterest(product, event.currentTarget));
      window.requestAnimationFrame(() => detail.querySelector('.jsk-auto-detail__glass')?.focus({ preventScroll: true }));
    };

    search.addEventListener('input', () => { state.query = search.value.trim().toLowerCase(); state.page = 1; render(); });
    select.addEventListener('change', () => { state.taxonomy = select.value; state.page = 1; render(); });
    groups.addEventListener('click', (event) => {
      const button = event.target.closest('[data-group]');
      if (!button) return;
      state.taxonomy = button.dataset.group;
      state.page = 1;
      render();
    });
    clear.addEventListener('click', () => { search.value = ''; state.query = ''; state.taxonomy = 'all'; state.page = 1; render(); });
    pagination.addEventListener('click', (event) => {
      const button = event.target.closest('[data-page]');
      if (!button || button.disabled) return;
      state.page = Number(button.dataset.page);
      render();
      root.querySelector('.jsk-auto-catalog__summary').scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    });
    grid.addEventListener('click', (event) => {
      const next = event.target.closest('[data-media-next]');
      const prev = event.target.closest('[data-media-prev]');
      if (next || prev) {
        const code = (next || prev).dataset.mediaNext || (next || prev).dataset.mediaPrev;
        const product = data.products.find((item) => item.code === code);
        const current = state.media.get(code) || 0;
        state.media.set(code, next ? (current + 1) % product.media.length : (current - 1 + product.media.length) % product.media.length);
        render();
        return;
      }
      const action = event.target.closest('[data-interest-code]');
      if (!action) return;
      const product = data.products.find((item) => item.code === action.dataset.interestCode && item.register_interest_safe);
      openInterest(product, action);
    });
    interest.addEventListener('click', (event) => { if (event.target === interest || event.target.closest('[data-auto-interest-close]')) closeInterest(); });
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && !interest.hidden) closeInterest(); });

    const requestedCode = new URL(window.location.href).searchParams.get('product')?.trim().toLowerCase();
    const requestedProduct = requestedCode ? data.products.find((product) => product.code.toLowerCase() === requestedCode) : null;
    if (requestedProduct) renderDetail(requestedProduct);
    else render();
  });
})();
